import type { RumErrorEvent, RumEvent } from '@datadog/browser-rum';
import { getValidUserId } from 'utils/storageUtils';
import { storage, StorageKey } from 'utils/typedStorage';

import {
  isCssPreloadErrorMessage,
  isDynamicImportFailureMessage,
} from './chunkLoadErrorMessage';
import { getInterviewRumContext } from './interviewRumContext';
import { isSharedPagePath } from './sharedPagePath';
export interface CandidateRumContext {
  user_type: 'candidate';
  session_stage: string;
  id?: string;
  candidate_id?: string;
  campaign_id?: string;
  call_id?: string;
  name?: string;
  is_test_call?: boolean;
  encoded_id?: string;
  encoded_test_data?: string;
  url_path?: string;
  url_params?: string;
  timestamp_loaded?: string;
  timestamp_component_mount?: string;
  [key: string]: unknown;
}

/** What the web-call flow knows about the candidate when it tags the RUM user. */
export interface DatadogUserContextParams {
  candidateId: string;
  campaignId?: string;
  callId?: string | null;
  candidateName?: string;
  isTestCall?: boolean;
}

export const isAuthenticatedUser = (): boolean => {
  const userId = getValidUserId();
  const userEmail = storage.get(StorageKey.Email);
  return !!(userId && userEmail);
};

// Vite emits the RUM SDK as `datadog-*` chunks (`assets/datadog-*.js`): the
// eager SDK chunk plus the lazily fetched recorder and profiler chunks.
const LEGACY_DATADOG_CHUNK_HTTPS_MATCH =
  /https?:\/\/[^/\s]+\/assets\/datadog-[^:\s]+/g;

const HTTPS_FRAME = /https?:\/\/[^\s]+/g;

// Legacy filtering inspects URL substrings from any HTTP(S) host. New rules
// require complete production frame locations; do not interchange the policies.
const DATADOG_FRAME_PATTERNS = {
  legacy: /\/assets\/datadog-/,
  production:
    /^https:\/\/(?:interview|app)\.tenzo\.ai\/assets\/datadog-[\w-]+\.js:\d+:\d+$/,
};

const stackContainsOnlyAllowedFrames = (
  frames: readonly (string | undefined)[],
  allowedPatterns: readonly RegExp[]
): boolean =>
  frames.length > 0 &&
  frames.every(
    (frame) =>
      frame !== undefined &&
      allowedPatterns.some((pattern) => pattern.test(frame))
  );

// Extension content scripts that wrap appendChild and JSON.stringify a React-
// managed DOM node produce this shape. The throw is in <anonymous> frames; the
// only first-party HTTPS frames are often our Datadog vendor chunk (reporting /
// handling), which would otherwise defeat the generic https? filter below.
const CIRCULAR_DOM_FIBER_ERROR =
  /circular structure to JSON[\s\S]*HTML\w*Element[\s\S]*(?:__reactFiber|Fiber)/i;

const ANONYMOUS_APPENDCHILD_STRINGIFY =
  /JSON\.stringify[\s\S]*appendChild[\s\S]*<anonymous>|<anonymous>[\s\S]*JSON\.stringify[\s\S]*appendChild/i;

const isDatadogOnlyHttpsStack = (stack: string): boolean => {
  const httpsFrames = stack.match(HTTPS_FRAME) ?? [];
  const datadogFrames = stack.match(LEGACY_DATADOG_CHUNK_HTTPS_MATCH) ?? [];
  // Preserve the legacy global match-count guard: a URL substring check alone
  // also accepts nested paths or multiple Datadog URLs inside one HTTP(S) match.
  return (
    httpsFrames.length === datadogFrames.length &&
    stackContainsOnlyAllowedFrames(httpsFrames, [DATADOG_FRAME_PATTERNS.legacy])
  );
};

const isExtensionDomSerializeNoise = (stack: string): boolean => {
  if (!CIRCULAR_DOM_FIBER_ERROR.test(stack)) return false;
  if (!stack.includes('<anonymous>')) return false;
  if (!ANONYMOUS_APPENDCHILD_STRINGIFY.test(stack)) return false;
  return isDatadogOnlyHttpsStack(stack);
};

const IMAGE_ASSISTANT_FRAME =
  /^chrome-extension:\/\/dbjbempljhcmhlfpfacalomonjpalpko\/scripts\/inspector\.js:\d+:\d+$/;
const GOOGLE_TRANSLATE_FRAME =
  /^https:\/\/translate\.googleapis\.com\/(?:translate_static\/|element\/|_\/translate_http\/_\/js\/)/;

// Accept complete frame lines, not just URLs found somewhere in the stack.
// An unrecognized frame (including anonymous/eval work) keeps the event visible.
const frameLocation = (line: string): string | undefined => {
  const match = line.trim().match(/^at (?:.* @ |.* \()?([^\s()]+)\)?$/);
  return match?.[1];
};

const isImageAssistantTelemetryNoise = (event: RumEvent): boolean => {
  if (event.type !== 'error') return false;
  const error = event.error;
  if (
    error.source !== 'console' ||
    error.message !== 'Fetch request failed: TypeError: Failed to fetch' ||
    !error.stack?.startsWith('TypeError: Failed to fetch\n') ||
    !error.handling_stack?.startsWith('HandlingStack: console error\n')
  ) {
    return false;
  }
  const handlingFrames = error.handling_stack
    .split('\n')
    .slice(1)
    .map(frameLocation);
  if (
    !stackContainsOnlyAllowedFrames(handlingFrames, [IMAGE_ASSISTANT_FRAME])
  ) {
    return false;
  }
  const frames = error.stack.split('\n').slice(1).map(frameLocation);
  return (
    frames.length > 1 &&
    IMAGE_ASSISTANT_FRAME.test(frames[0] ?? '') &&
    frames.some((frame) =>
      DATADOG_FRAME_PATTERNS.production.test(frame ?? '')
    ) &&
    stackContainsOnlyAllowedFrames(frames, [
      IMAGE_ASSISTANT_FRAME,
      DATADOG_FRAME_PATTERNS.production,
    ])
  );
};

interface ErrorClassification {
  error_origin: 'google_translate';
  classification_confidence: 'high' | 'medium';
  classification_reason: string;
}

const classifyTranslationError = (
  event: RumEvent
): ErrorClassification | undefined => {
  if (event.type !== 'error') return undefined;
  const stack = event.error.stack ?? '';
  const frameLines = stack.split('\n').slice(1);
  const frames = frameLines.map(frameLocation);
  if (
    GOOGLE_TRANSLATE_FRAME.test(frames[0] ?? '') &&
    stackContainsOnlyAllowedFrames(frames, [
      GOOGLE_TRANSLATE_FRAME,
      DATADOG_FRAME_PATTERNS.production,
    ])
  ) {
    return {
      error_origin: 'google_translate',
      classification_confidence: 'high',
      classification_reason: 'google_translate_script_in_stack',
    };
  }

  // Root classes are Google's page-translation marker. This is a constant-time
  // check; do not scan the DOM or resource timings on every RUM event. Inline
  // frame signatures are version-specific, so this attribution is tentative.
  const root =
    typeof document === 'undefined' ? undefined : document.documentElement;
  const translated =
    root?.classList.contains('translated-ltr') ||
    root?.classList.contains('translated-rtl');
  const inlineFrames = frames.map((frame) =>
    frame?.match(
      /^(https:\/\/interview\.tenzo\.ai\/(?:web-interview|test-call)\/[^?#\s:]+):(\d+):(\d+)$/
    )
  );
  if (
    translated &&
    event.error.message === 'La' &&
    frames.length === 3 &&
    inlineFrames.every((frame) => frame && frame[1] === inlineFrames[0]?.[1]) &&
    /(?:^|\s)Vi(?: @ | \()/.test(frameLines[1] ?? '') &&
    inlineFrames[0]?.[2] === '476' &&
    inlineFrames[0]?.[3] === '363' &&
    inlineFrames[1]?.[2] === '194' &&
    inlineFrames[1]?.[3] === '41' &&
    inlineFrames[2]?.[2] === '195' &&
    inlineFrames[2]?.[3] === '338'
  ) {
    return {
      error_origin: 'google_translate',
      classification_confidence: 'medium',
      classification_reason: 'translated_page_and_known_inline_la_signature',
    };
  }
  return undefined;
};

// Browser extensions and other injected scripts that reach into the candidate's
// page produce errors we drop in beforeSend: extension-scheme URLs, stacks with
// only `<anonymous>` frames, or (see isExtensionDomSerializeNoise) circular
// HTML*Element/fiber stringify failures whose only https frames are our
// datadog-*.js vendor chunk. Real app errors still report when a non-datadog
// https frame is present.
export const isThirdPartyError = (event: RumEvent): boolean => {
  if (event.type !== 'error') return false;
  if (isImageAssistantTelemetryNoise(event)) return true;
  const stack = event.error.stack;
  if (!stack) return false;

  // Extension appendChild + JSON.stringify(HTML*Element/fiber) with only
  // datadog-*.js https frames — keep this before the generic https? keep-rule.
  if (isExtensionDomSerializeNoise(stack)) return true;

  // Any http(s) frame means our served bundle is on the stack, so keep it even
  // if an extension also appears — an extension-triggered throw in our code is
  // still a real app error.
  if (/https?:\/\//.test(stack)) return false;
  if (/(?:chrome|moz|safari|safari-web)-extension:\/\//.test(stack)) {
    return true;
  }
  return stack.includes('<anonymous>');
};

const BROWSER_REPORTED_ERROR_SOURCES = new Set<
  RumErrorEvent['error']['source']
>(['source', 'console']);

const LOCAL_RUM_HOSTS = new Set(['localhost', '127.0.0.1', '::1']);

// Only after the PDF viewer prefix. A bare "Failed to fetch" is also how a
// real API call dies, and WebKit's "Load failed" is too common to drop alone.
const OFFLINE_DOCUMENT_READ_FAILURE =
  /(?:^|[\s:])(?:Failed to fetch|NetworkError when attempting to fetch resource|Load failed)\b/i;

const DOCUMENT_READ_PREFIX = 'Error reading file type';

const isLocalHostname = (hostname: string): boolean => {
  // Node reports IPv6 hostnames with brackets (`[::1]`); browsers omit them.
  const host = hostname.toLowerCase().replace(/^\[|\]$/g, '');
  return LOCAL_RUM_HOSTS.has(host) || host.endsWith('.localhost');
};

// Dev HMR crashes share the production RUM application. Key off the view URL
// on the event, not window.location: tests run in jsdom on localhost, and
// fixtures that omit view.url must keep their existing keep/drop result.
const isLocalhostRumView = (event: RumErrorEvent): boolean => {
  const rawUrl = event.view?.url;
  if (typeof rawUrl !== 'string' || rawUrl.length === 0) return false;
  try {
    return isLocalHostname(new URL(rawUrl).hostname);
  } catch {
    // swallow-ok: Malformed RUM view URLs never qualify as localhost; the telemetry event is retained by beforeSend rather than discarded.
    return false;
  }
};

const rumErrorText = (error: RumErrorEvent['error']): string => {
  const stackFirstLine = error.stack?.split('\n')[0];
  const parts = [
    error.message,
    stackFirstLine,
    ...(error.causes ?? []).map((cause) => cause.message),
  ];
  return parts
    .filter(
      (part): part is string => typeof part === 'string' && part.length > 0
    )
    .join('\n');
};

// Browser-collected and console-forwarded copies of a chunk failure. The
// reload path records an action, and unrecovered paths call addError (source
// custom), which is kept. Match the Vite/browser phrase, not a call-site prefix,
// so "Error taking screenshot: …dynamically imported module…" drops and a
// screenshot SecurityError does not.
const isBrowserChunkLoadNoise = (event: RumErrorEvent): boolean => {
  if (!BROWSER_REPORTED_ERROR_SOURCES.has(event.error.source)) return false;
  return (
    isDynamicImportFailureMessage(rumErrorText(event.error)) ||
    isCssPreloadErrorMessage(event.error.message)
  );
};

const isOfflineDocumentReadNoise = (event: RumErrorEvent): boolean => {
  if (event.error.source !== 'console') return false;
  if (!event.error.message.startsWith(DOCUMENT_READ_PREFIX)) return false;
  const remainder = event.error.message.slice(DOCUMENT_READ_PREFIX.length);
  const causeText = (event.error.causes ?? [])
    .map((cause) => cause.message)
    .join('\n');
  return (
    OFFLINE_DOCUMENT_READ_FAILURE.test(remainder) ||
    OFFLINE_DOCUMENT_READ_FAILURE.test(causeText)
  );
};

const attachRumContext = (event: RumErrorEvent): void => {
  const classification = classifyTranslationError(event);
  const interview = getInterviewRumContext();
  event.context = {
    ...event.context,
    ...classification,
    ...(interview ? { interview } : {}),
  };
};

/** Pure: `url` without its fragment when it is a share-link page; any other URL unchanged. */
export const stripSharedPageFragment = (url: string): string => {
  const hashIndex = url.indexOf('#');
  if (hashIndex === -1) return url;
  let pathname: string;
  try {
    pathname = new URL(url, 'https://placeholder.invalid').pathname;
  } catch {
    // Unparseable, so whether it is a share page is unknown: drop the fragment to be safe.
    return url.slice(0, hashIndex);
  }
  return isSharedPagePath(pathname) ? url.slice(0, hashIndex) : url;
};

/** Remove a share-link secret from the URLs an event records. */
const scrubSharedPageFragments = (event: RumEvent): void => {
  // Fixtures and some SDK paths omit view, though the type says it is always set.
  const view: Partial<RumEvent['view']> | undefined = event.view;
  if (view && typeof view.url === 'string') {
    view.url = stripSharedPageFragment(view.url);
  }
  if (view && typeof view.referrer === 'string') {
    view.referrer = stripSharedPageFragment(view.referrer);
  }
  if (event.type === 'resource' && typeof event.resource.url === 'string') {
    event.resource.url = stripSharedPageFragment(event.resource.url);
  }
};

// Preserve original errors and caller context while adding investigation fields.
// Translation attribution never suppresses an event or downgrades its severity.
// addError reports (source custom) are kept on every non-local view, including
// unrecovered chunk failures, unless the stack is third-party. Localhost is
// dropped first so a local addError does not land in the shared production
// error stream. Every event first loses a share-link secret from its URLs.
export const beforeSendRumEvent = (event: RumEvent): boolean => {
  scrubSharedPageFragments(event);
  if (event.type !== 'error') return true;
  if (isLocalhostRumView(event)) return false;
  if (isThirdPartyError(event)) return false;
  if (event.error.source !== 'custom') {
    if (isBrowserChunkLoadNoise(event)) return false;
    if (isOfflineDocumentReadNoise(event)) return false;
  }
  attachRumContext(event);
  return true;
};
